import { useEffect, useState, type ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Sidebar, type SidebarMe } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { ProfileDrawer } from '@/components/profile/ProfileDrawer';
import {
  StackedBanners,
  type StackedBannersProps,
} from '@/components/layout/StackedBanners';
import { AIAssistantFAB } from '@/components/AIAssistant/AIAssistantFAB';
import { api, tryRefresh } from '@/lib/api';
import {
  clearImpersonationSnapshot,
  readImpersonationSnapshot,
  setImpersonationActiveFromServer,
} from '@/lib/auth/impersonationSession';
import { cn } from '@/lib/utils';
import { captureException } from '@/lib/sentry';
import { toast } from '@/lib/toast';
import { describeApiError } from '@/lib/apiError';
import { useTilePrewarmEffect } from '@/lib/map/useTilePrewarmEffect';

import { exitClientWindow } from '@/lib/consultant/api';
import {
  invalidateConsultantProfile,
  useConsultantProfile,
} from '@/lib/consultant/useConsultantProfile';

interface Me extends SidebarMe {
  active_tenant?: {
    id: string;
    name: string;
    access_state?: 'active' | 'trial' | 'grace' | 'restricted' | 'suspended';
    access_state_until?: string | null;
  };
  /**
   * Открытый чужой кабинет (админ вошёл в компанию, консультант работает по
   * гранту). Для доступа к данным он важнее собственного: сервер считает
   * рабочим `viewing OR active` — поэтому гейт «выберите кабинет» на него
   * смотреть обязан, иначе выкидывал бы человека из чужого кабинета обратно
   * в выбор своего.
   */
  viewing_tenant?: { id: string; name: string } | null;
  impersonation?: {
    is_impersonating: boolean;
    tenant_name?: string | null;
    expires_at?: string | null;
    /**
     * AK-470 — под кем работают и что при этом разрешено. Приходит из
     * claim'ов токена (PR #578), а значит в любой вкладке.
     */
    user_full_name?: string | null;
    mode?: 'read' | 'write' | null;
  } | null;
  must_setup_2fa?: boolean;
  should_remind_2fa?: boolean;
  /** У пользователя нет пароля — до его установки в приложение не пускаем. */
  must_set_password?: boolean;
}

interface CompanyInfo {
  id: string;
  name: string;
  short_name?: string | null;
  logo_url?: string | null;
}

/**
 * AppShell — root layout: sidebar | (TopBar + StackedBanners + Content) + FAB.
 *
 * Mobile (<768px): sidebar в Sheet (drawer, развёрнутый 220px); на десктопе
 * (≥768) — ВСЕГДА рельс 72px (иконки с тултипами), на любой ширине.
 *
 * AK-974: авторазворот 220px на ≥1366 (#1109) откачен — это было изменение
 * оболочки без approve founder. Вид оболочки по умолчанию меняется только с
 * approve founder (frontend-vite/CLAUDE.md «Изменение оболочки»).
 *
 * Listens to /api/v1/auth/me, redirects to /login on 401 (api.ts уже делает
 * это для всех api.* вызовов, но первичный fetch /auth/me — точка входа).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [me, setMe] = useState<Me | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyShortName, setCompanyShortName] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Снимок нужен ровно на первый кадр вкладки, где в режим вошли (до ответа
  // /auth/me). Дальше слово сервера главнее — см. эффект ниже.
  const [snapshot, setSnapshot] = useState(() => readImpersonationSnapshot());
  // Открыто ли окно клиента. Отдельный от impersonation признак: там
  // платформенный админ входит под пользователем, здесь консультант работает
  // в данных клиента по гранту — разные механики и разные тексты.
  const { profile: consultantProfile } = useConsultantProfile();
  const clientWindow = consultantProfile?.client_window ?? null;
  const inClientWindow = !!clientWindow;

  // Track viewport — sidebar in drawer mode на mobile (<768px), на десктопе —
  // рельс 72px на любой ширине (AK-974).
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load /auth/me. На 401 api.ts сам делает window.location.href = '/login'.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await api.get<Me>('/api/v1/auth/me');
        if (cancelled) return;
        if (m.must_setup_2fa) {
          window.location.href = '/auth/2fa-required';
          return;
        }
        // Пароля нет — человек пришёл по приглашению и живёт на одной этой
        // сессии. Закроет вкладку — войти второй раз будет нечем: форма
        // смены пароля в профиле спрашивает текущий, которого не существует.
        // Гейт такой же жёсткий, как у 2FA: обойти нельзя, пропустить нечем.
        // `next` — туда, куда человек шёл, чтобы после пароля не терять место.
        if (m.must_set_password) {
          const here = window.location.pathname + window.location.search;
          window.location.href = `/onboarding/set-password?next=${encodeURIComponent(here)}`;
          return;
        }
        // AK-660: кабинет не выбран, а членства есть — рабочих данных не будет
        // ни на одном экране (все scope-запросы отвечают 403). Раньше человек
        // с двумя кабинетами застревал на «Загружаем…» навсегда; теперь его
        // ведут туда, где выбор наконец существует.
        if (!m.active_tenant && !m.viewing_tenant && (m.memberships?.length ?? 0) > 0) {
          window.location.href = '/auth/select-tenant';
          return;
        }
        // Слово сервера про режим — единственное, что действует после
        // ответа. Сказал «режима нет» — снимок стираем, иначе он переживёт
        // конец режима (падение браузера, восстановленная вкладка) и плашка
        // соврёт в другую сторону: человек под собой, а на экране написано,
        // что под чужой учёткой.
        const active = !!m.impersonation?.is_impersonating;
        setImpersonationActiveFromServer(active);
        if (!active) {
          clearImpersonationSnapshot();
          setSnapshot(null);
        }
        setMe(m);
      } catch (err) {
        // api.ts уже редиректит на /login при 401.
        if (import.meta.env.DEV) console.error('[appshell] /auth/me failed', err);
        else captureException(err, { tags: { source: 'AppShell.authMe' } });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load active company logo / short name (для левого блока TopBar).
  useEffect(() => {
    api
      .get<CompanyInfo[]>('/api/v1/companies')
      .then((cs) => {
        const c = cs[0];
        setCompanyLogo(c?.logo_url ?? null);
        setCompanyShortName(c?.short_name ?? null);
        setCompanyName(c?.name ?? null);
      })
      .catch(() => {
        /* tolerate */
      });
  }, []);

  // Proactive auth keepalive — 12 мин interval + visibilitychange.
  // (frontend/src/components/AppShell.tsx, юзер 2026-04-27, AK-32).
  //
  // AK-79: ходим ТОЛЬКО через shared tryRefresh() lock из api.ts. Раньше
  // здесь был сырой fetch — он race'ился с interceptor-refresh из api.ts
  // (две вкладки эффектов с одной cookie R1 → backend reuse-detection
  // revoke-ал сессию → silent 401 каскадом, юзер залипал на UI).
  // tryRefresh() гарантирует что в полёте только один refresh-вызов.
  // На неудачу — редиректим на /login (раньше silent swallow).
  //
  // AK-470: режиму «вы работаете под …» keepalive больше не мешает. Сервер
  // обновляет сессию с оглядкой на режим: пока окно открыто — перевыпускает
  // токен режима, не проворачивая refresh админа; когда окно вышло или
  // сессию режима отозвали — сам закрывает режим и говорит об этом
  // заголовком `X-Impersonation-State: ended`, который api.ts превращает в
  // уход из режима. Поэтому keepalive снова работает одинаково для всех:
  // гейт, что стоял здесь до контракта, теперь только обрывал бы режим на
  // половине окна (см. lib/api.ts).
  useEffect(() => {
    const proactive = async () => {
      const ok = await tryRefresh();
      if (!ok) {
        // refresh умер (cookie истекла, или session revoked) — на /login.
        // Не редиректим если уже на публичной странице — чтобы не сломать
        // /login, /auth/error и т.п.
        const path = window.location.pathname;
        const publicPath =
          path.startsWith('/login') ||
          path.startsWith('/auth/') ||
          path.startsWith('/tenant-suspended');
        if (!publicPath) {
          window.location.href = '/login';
        }
      }
    };
    const interval = setInterval(() => void proactive(), 12 * 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void proactive();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // ADR-037 pre-warm B: once authenticated, warm the user's first-farm × latest
  // NDVI date tiles in the background (idle, low-priority, detached Image GETs)
  // so the first «Карта» click paints from cache. No-op on /map (shared cache),
  // skipped on Save-Data / no farms. Never touches a live MapLibre source.
  useTilePrewarmEffect(!!me);

  const navigate = (path: string) => {
    router.navigate({ to: path });
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } finally {
      // Иначе снимок доживёт до следующего входа в этой же вкладке и
      // плашка соврёт человеку, что он работает под чужой учёткой.
      clearImpersonationSnapshot();
      window.location.href = '/login';
    }
  };

  // AK-660: выпадашка кабинетов в шапке была витриной — клик по пункту не
  // делал ничего, потому что серверной ручки не существовало. Теперь пункт
  // действительно меняет кабинет.
  //
  // AK-664: из открытого чужого кабинета кнопка молчала. Ручка отвечает 409
  // («сначала выйдите из режима») — и это верная семантика: перенести режим в
  // новый токен нельзя. Но человеку от честного отказа сервера не легче, если
  // отказ уходит в консоль: founder нажимал и «ничего не происходило».
  // Поэтому оболочка сама проводит человека по цепочке — выйти из чужого
  // кабинета, затем выбрать свой, — а любой сбой на любом шаге показывает
  // тостом текстом бэка, а не молчит.
  //
  // После смены — полная перезагрузка и увод на дашборд: всё загруженное на
  // клиенте (поля, сезон, справочники) принадлежит прошлому кабинету, а id в
  // текущем URL в новом кабинете просто нет.
  const handleSwitchTenant = async (tenantId: string) => {
    const inViewingMode =
      !!me?.viewing_tenant || !!me?.impersonation?.is_impersonating;
    // Тот же кабинет и никакого чужого окна — делать нечего. А вот «тот же»
    // ИЗ окна клиента — осмысленный выбор: это и есть «вернуться к себе».
    if (tenantId === me?.active_tenant?.id && !inClientWindow && !inViewingMode) {
      return;
    }
    // Первый шаг цепочки уже отработал — чужой кабинет на сервере закрыт.
    // Если упадёт второй, экран останется прежним: плашка «Вы в кабинете
    // клиента» на месте, хотя окна больше нет. Индикатор, который врёт про
    // чужие данные, опаснее самой ошибки — поэтому после тоста перечитываем
    // страницу: `/auth/me` и профиль отдадут честное состояние.
    let exited = false;
    try {
      if (inClientWindow) {
        await exitClientWindow();
        invalidateConsultantProfile();
        exited = true;
      } else if (inViewingMode) {
        await api.post('/api/v1/auth/exit-impersonation');
        clearImpersonationSnapshot();
        exited = true;
      }
      await api.post('/api/v1/auth/select-tenant', { tenant_id: tenantId });
    } catch (err) {
      toast.error(describeApiError(err));
      if (import.meta.env.DEV) console.error('[appshell] switch tenant failed', err);
      else captureException(err, { tags: { source: 'AppShell.switchTenant' } });
      if (exited) window.location.reload();
      return;
    }
    window.location.href = '/dashboard';
  };

  const handleExitImpersonation = async () => {
    try {
      await api.post('/api/v1/auth/exit-impersonation');
    } catch {
      /* tolerate */
    } finally {
      clearImpersonationSnapshot();
      window.location.href = '/platform-admin/companies';
    }
  };

  const pathname = router.state.location.pathname;

  const ownTenantName = me?.active_tenant?.name ?? null;
  const currentTenantName = ownTenantName ?? '—';
  // AK-664 (ориентировка). Внутри окна клиента `/companies` отдаёт компанию
  // КЛИЕНТА — сервер считает рабочим кабинет, который открыт (viewing поверх
  // active). Из-за этого левый блок шапки показывал имя и логотип клиента
  // ровно там, где человек читает «в чьём я кабинете», и дублировал плашку
  // «Клиент: …»: два одинаковых имени подряд и ни одного упоминания себя.
  // Отсюда ощущение «провалился в чужой кабинет и не понимаю, где я». В этом
  // режиме слева стоит СВОЙ кабинет, клиент — только в плашке рядом.
  const showOwnTenantInHeader = inClientWindow && !!ownTenantName;
  const shortTenantName =
    (showOwnTenantInHeader ? ownTenantName : null) ??
    (companyShortName?.trim() || companyName?.trim() || currentTenantName);
  const topBarLogo = showOwnTenantInHeader ? null : companyLogo;
  // Подпись «Холдинг — <имя>» в переключателе хозяйства (AK-895). Берём
  // `/companies`, а не своё имя: внутри окна клиента переключатель показывает
  // хозяйства КЛИЕНТА, и холдинг должен называться его именем.
  const scopeCompanyName = companyName?.trim() || ownTenantName;

  const tenants = me?.memberships ?? [];

  // Плашка «вы работаете под …» (AK-470).
  //
  // Ответ /auth/me пришёл — рисуем по нему и только по нему: признак собран
  // из claim'ов токена, поэтому плашка есть в любой вкладке, а не только в
  // той, где нажали «Войти под пользователем» (там жил sessionStorage).
  // Пока ответа нет — показываем снимок, чтобы первый кадр после входа в
  // режим не был «чистым».
  const impersonationInfo: StackedBannersProps['impersonation'] = me
    ? me.impersonation?.is_impersonating
      ? {
          is_impersonating: true,
          tenant_name: me.impersonation.tenant_name ?? snapshot?.tenant_name,
          user_full_name:
            me.impersonation.user_full_name ?? snapshot?.user_full_name ?? null,
          mode: me.impersonation.mode ?? snapshot?.mode ?? null,
        }
      : null
    : snapshot
      ? {
          is_impersonating: true,
          tenant_name: snapshot.tenant_name,
          user_full_name: snapshot.user_full_name,
          mode: snapshot.mode,
        }
      : null;

  const accessStateInfo: StackedBannersProps['accessState'] =
    me?.active_tenant?.access_state
      ? {
          state: me.active_tenant.access_state,
          until: me.active_tenant.access_state_until ?? null,
        }
      : null;

  return (
    <TooltipProvider delayDuration={300}>
      {/* h-dvh (dynamic viewport) вместо h-screen (100vh) — учитывает
          появление/скрытие нижней адресной строки мобильного браузера,
          иначе низ контента уезжает под неё (AK-374). */}
      <div className="flex h-dvh w-full overflow-hidden bg-background">
        {/* Desktop sidebar — рельс 72px (только иконки с тултипами) на любой
            ширине; разворот по умолчанию — только с approve founder (AK-974) */}
        {!isMobile && (
          <Sidebar
            me={me}
            pathname={pathname}
            onNavigate={navigate}
            onLogout={handleLogout}
            onOpenProfile={() => setProfileOpen(true)}
            collapsed
          />
        )}

        {/* Mobile drawer */}
        {isMobile && (
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
              side="left"
              className="p-0 w-[260px] sm:max-w-[260px]"
              hideClose
            >
              <SheetTitle className="sr-only">Меню</SheetTitle>
              <SheetDescription className="sr-only">
                Боковое меню навигации по приложению
              </SheetDescription>
              <Sidebar
                me={me}
                pathname={pathname}
                onNavigate={navigate}
                onLogout={handleLogout}
                onOpenProfile={() => {
                  setDrawerOpen(false);
                  setProfileOpen(true);
                }}
                collapsed={false}
              />
            </SheetContent>
          </Sheet>
        )}

        <div className="flex flex-1 flex-col overflow-hidden">
          <TopBar
            isMobile={isMobile}
            onOpenMobileSidebar={() => setDrawerOpen(true)}
            companyLogo={topBarLogo}
            shortTenantName={shortTenantName}
            currentTenantName={currentTenantName}
            tenants={tenants}
            onSwitchTenant={(id) => void handleSwitchTenant(id)}
            /* Контекст клиента живёт в шапке рядом с названием компании —
               там же, где человек и так читает «где я». Полоса во всю ширину
               с обратным отсчётом отсюда убрана: срока у окна больше нет
               (модель D), а тревожный тон на каждом экране только утомлял. */
            clientWindow={clientWindow}
            /* Куда возвращаться из окна клиента — своим именем, а не «к себе»:
               человек с двумя кабинетами должен видеть, в КАКОЙ он вернётся. */
            ownTenantName={ownTenantName}
            scopeCompanyName={scopeCompanyName}
          />

          <StackedBanners
            impersonation={impersonationInfo}
            onExitImpersonation={handleExitImpersonation}
            accessState={accessStateInfo}
            shouldRemind2FA={me?.should_remind_2fa}
            onSetup2FA={() => navigate('/settings/security/2fa')}
          />

          <main
            className={cn(
              'flex-1 overflow-y-auto bg-secondary/40',
              // СИММЕТРИЧНЫЙ p-3/p-6 — критично: /map эскейпит паддинг main
              // отрицательным margin (-m-3/-m-6 + calc(100%+24/48px)),
              // рассчитанным на симметрию. Асимметричный нижний паддинг
              // (pb-safe-fab) ломал карту: серая полоса под canvas + короткий
              // MapLibre (AK-374 regression). ФАБ-клиренс (pb-safe-fab)
              // навешивается адресно на скролл-контейнеры КОНТЕНТНЫХ страниц
              // с ФАБ (дашборд, детали поля/хозяйства, отчёты), НЕ на карту.
              isMobile ? 'p-3' : 'p-6',
            )}
          >
            {children}
          </main>
        </div>

        <AIAssistantFAB />

        <ProfileDrawer open={profileOpen} onOpenChange={setProfileOpen} />
      </div>
    </TooltipProvider>
  );
}

export default AppShell;
